import React, { useState, useEffect, useCallback } from 'react';
import { Table, Spin, Alert, Typography, Button, Modal, Form, Input, Select, Row, Col, message, Tabs } from 'antd'; // Added Tabs
import { UserAddOutlined, SearchOutlined, UserOutlined, TeamOutlined } from '@ant-design/icons'; // Added TeamOutlined
import axios from 'axios';
// Link is no longer needed for opening chart
// import { Link } from 'react-router-dom';
import moment from 'moment';
import { usePatientChart } from '../context/PatientChartContext'; // Import the context hook
import WeeklyIdtPatientList from '../components/WeeklyIdtPatientList'; // Import Weekly IDT List component
import MomentDatePicker from '../components/MomentDatePicker';

const { Title } = Typography;
const { Option } = Select;
const { Search } = Input; // Destructure Search component

const PatientListPage = () => {
  const { addChart, showChart, patientListRefreshTrigger } = usePatientChart(); // Get addChart, showChart, and refresh trigger from context
  const [patients, setPatients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [form] = Form.useForm(); // Get form instance for Add modal
  const [searchTerm, setSearchTerm] = useState(''); // State for search term
  const [activeTab, setActiveTab] = useState('patient-list'); // State for active tab

  // Use useCallback to memoize fetchPatients function
  const fetchPatients = useCallback(async (searchValue = '') => { // Accept search term
    setError(null);
    setLoading(true);
    try {
      // Construct query parameters
      const params = {};
      if (searchValue) {
        params.search = searchValue;
      }

      const response = await axios.get('/patients', { params }); // Removed prefix
      const patientsRaw = response.data.data.patients;
      // Sort by updatedAt: items with updatedAt go to the top (descending by time), items with updatedAt == null stay at the bottom and keep their original order
      const withUpdatedAt = [];
      const withoutUpdatedAt = [];
      patientsRaw.forEach(item => {
        if (item.updatedAt) {
          withUpdatedAt.push(item);
        } else {
          withoutUpdatedAt.push(item);
        }
      });
      withUpdatedAt.sort((a, b) => new Date(b.updatedAt) - new Date(a.updatedAt));

      setPatients([...withUpdatedAt, ...withoutUpdatedAt]);
    } catch (err) {
      console.error("Error fetching patients:", err);
      setError(err.response?.data?.message || 'Failed to fetch patients. Ensure you are logged in and the backend is running.');
    } finally {
      setLoading(false);
    }
  }, []); // Empty dependency array, function doesn't change based on props/state outside its scope

  useEffect(() => {
    fetchPatients(searchTerm); // Fetch initially and when searchTerm changes
  }, [fetchPatients, searchTerm, patientListRefreshTrigger]); // Also fetch when refresh trigger changes

  const showAddModal = () => {
    form.resetFields(); // Reset form fields when opening modal
    setError(null); // Clear previous errors
    setIsModalVisible(true);
  };

  const handleCancel = () => {
    setIsModalVisible(false);
  };

  const handleAddPatient = async (values) => {
    console.log('Submitting new patient:', values);
    setLoading(true); // Indicate loading state during submission
    setError(null);

    // Format date correctly if needed, though backend should handle ISO string
    const payload = {
      ...values,
      dateOfBirth: values.dateOfBirth ? values.dateOfBirth.toISOString() : null,
    };

    // Move relationshipStatus to structuredLifestyleHistory if it exists
    if (payload.relationshipStatus) {
      payload.structuredLifestyleHistory = {
        relationshipStatus: payload.relationshipStatus
      };
      delete payload.relationshipStatus;
    }

    try {
      const response = await axios.post('/patients', payload); // Removed prefix
      const newPatient = response.data.data.patient;
      message.success({
        content: `Patient "${newPatient.firstName} ${newPatient.lastName}" added successfully!`,
        duration: 4,
        style: {
          marginTop: '20vh',
        },
      });
      // Small delay to ensure user sees the success message before modal closes
      setTimeout(() => {
        setIsModalVisible(false); // Close modal after showing success message
      }, 500);
      fetchPatients(searchTerm); // Refresh the patient list, preserving search term
    } catch (err) {
      console.error("Error adding patient:", err);
      console.error("Error response data:", err.response?.data);
      console.error("Error response status:", err.response?.status);
      console.error("Payload sent:", payload);
      setError(err.response?.data?.message || 'Failed to add patient.');
      message.error(err.response?.data?.message || 'Failed to add patient.');
    } finally {
      setLoading(false); // Stop loading indicator
    }
  };

  // Handle search input
  const handleSearch = (value) => {
    setSearchTerm(value); // Update search term state, triggering useEffect
  };

  // Define table columns
  const columns = [
    {
      title: 'Last Name',
      dataIndex: 'lastName',
      key: 'lastName',
      sorter: (a, b) => a.lastName.localeCompare(b.lastName),
    },
    {
      title: 'First Name',
      dataIndex: 'firstName',
      key: 'firstName',
      sorter: (a, b) => a.firstName.localeCompare(b.firstName),
    },
    {
      title: 'Middle Name',
      dataIndex: 'middleName',
      key: 'middleName',
      render: (middleName) => middleName || 'N/A',
      sorter: (a, b) => (a.middleName || '').localeCompare(b.middleName || ''),
    },
    {
      title: 'Date of Birth',
      dataIndex: 'dateOfBirth',
      key: 'dateOfBirth',
      render: (dob) => dob ? moment(dob).format('DD/MM/YYYY') : 'N/A', // Format date
    },
    {
      title: 'Sex',
      dataIndex: 'sex',
      key: 'sex',
    },
    {
        title: 'MRN',
        dataIndex: 'mrn',
        key: 'mrn',
        render: (mrn) => mrn || 'N/A',
    },
    {
      title: 'Actions',
      key: 'actions',
      render: (_, record) => (
        <Button type="link" onClick={() => {
          addChart(record);
          showChart();
        }}>
          Open Chart
        </Button>
      ),
    },
  ];

  // Tab items configuration
  const tabItems = [
    {
      key: 'patient-list',
      label: (
        <span>
          <UserOutlined />
          Patient List
        </span>
      ),
      children: (
        <div>
          {/* Display fetch error */}
          {error && !isModalVisible && <Alert message={error} type="error" showIcon style={{ marginBottom: 16 }} />}
          <Row justify="space-between" align="middle" style={{ marginBottom: 16 }}>
             <Col>
                <Button type="primary" icon={<UserAddOutlined />} onClick={showAddModal}>
                  Add New Patient
                </Button>
             </Col>
             <Col>
                <Search
                  placeholder="Search by name (first, middle, last, full name), MRN..."
                  allowClear
                  onSearch={handleSearch}
                  style={{ width: 350 }}
                  enterButton={<Button icon={<SearchOutlined />} />}
                />
             </Col>
          </Row>

          {/* Patient List Table */}
          <Spin spinning={loading && !isModalVisible}> {/* Show spinner only during fetch operation */}
            <Table
              columns={columns}
              dataSource={patients}
              rowKey="_id" // Use MongoDB's _id as the unique key
              pagination={{ pageSize: 10 }} // Optional: Add pagination
            />
          </Spin>
        </div>
      ),
    },
    {
      key: 'weekly-idt-list',
      label: (
        <span>
          <TeamOutlined />
          Weekly IDT List
        </span>
      ),
      children: <WeeklyIdtPatientList />,
    },
  ];

  return (
    <div>
      <Title level={2}>Patients</Title>

      <Tabs
        activeKey={activeTab}
        onChange={setActiveTab}
        items={tabItems}
        size="large"
        style={{ marginTop: 16 }}
      />

      {/* Add Patient Modal */}
      <Modal
        title="Add New Patient"
        visible={isModalVisible}
        onCancel={handleCancel}
        footer={null} // Footer handled by Form buttons
        destroyOnClose // Reset form state when modal is closed
      >
         {/* Display add error inside modal */}
         {error && isModalVisible && <Alert message={error} type="error" showIcon style={{ marginBottom: 16 }} />}
         <Spin spinning={loading && isModalVisible}> {/* Show spinner only during add operation */}
            <Form form={form} layout="vertical" onFinish={handleAddPatient}>
              <Row gutter={16}>
                <Col span={8}>
                  <Form.Item name="firstName" label="First Name" rules={[{ required: true, message: 'Please input first name!' }]}>
                    <Input />
                  </Form.Item>
                </Col>
                <Col span={8}>
                  <Form.Item name="middleName" label="Middle Name">
                    <Input />
                  </Form.Item>
                </Col>
                <Col span={8}>
                  <Form.Item name="lastName" label="Last Name" rules={[{ required: true, message: 'Please input last name!' }]}>
                    <Input />
                  </Form.Item>
                </Col>
              </Row>
              <Row gutter={16}>
                 <Col span={12}>
                    <Form.Item name="dateOfBirth" label="Date of Birth" rules={[{ required: true, message: 'Please select date of birth!' }]}>
                      <MomentDatePicker style={{ width: '100%' }} />
                    </Form.Item>
                 </Col>
                 <Col span={12}>
                    <Form.Item name="sex" label="Sex" rules={[{ required: true, message: 'Please select sex!' }]}>
                      <Select placeholder="Select sex">
                        <Option value="Male">Male</Option>
                        <Option value="Female">Female</Option>
                      </Select>
                    </Form.Item>
                 </Col>
              </Row>
               <Row gutter={16}>
                 <Col span={12}>
                   <Form.Item name="mrn" label="Medical Record Number (MRN)" rules={[{ required: true, message: 'Please input MRN!' }]}>
                     <Input />
                   </Form.Item>
                 </Col>
                 <Col span={12}>
                   <Form.Item name="relationshipStatus" label="Relationship Status">
                     <Select placeholder="Select relationship status" allowClear>
                       <Option value="Single">Single</Option>
                       <Option value="Married">Married</Option>
                       <Option value="Divorced">Divorced</Option>
                       <Option value="Widowed">Widowed</Option>
                       <Option value="Separated">Separated</Option>
                       <Option value="Domestic Partnership">Domestic Partnership</Option>
                       <Option value="In a Relationship">In a Relationship</Option>
                       <Option value="Prefer not to say">Prefer not to say</Option>
                     </Select>
                   </Form.Item>
                 </Col>
               </Row>
               {/* Add more fields as needed (Contact Info, Insurance, etc.) */}
              <Form.Item>
                <Button type="primary" htmlType="submit" loading={loading && isModalVisible}>
                  Add Patient
                </Button>
                <Button style={{ marginLeft: 8 }} onClick={handleCancel}>
                  Cancel
                </Button>
              </Form.Item>
            </Form>
         </Spin>
      </Modal>
    </div>
  );
};

export default PatientListPage;
